import { Injectable } from '@angular/core';

import { storeService } from "@unpispas/upp-base";
import { GenericUtils } from "@unpispas/upp-defs";

/**
 * Represents the type of storage, either for metadata or data.
 */
type StorageType = 'META' | 'DATA';

/**
 * Abstract base class for a storage system.
 * Defines the common interface for various storage implementations.
 */
export abstract class StorageDDBB {
    protected _isopen = false;

    /**
     * Returns true if the database is open
     */      
    get IsOpen(): boolean{
        return this._isopen;
    }

    /**
     * Retrieves the name of the database.
     */      
    abstract get ddbbname(): string;

    /**
     * Opens the storage for operations.
     * @returns A promise that resolves to `true` if the storage opens successfully.
     */    
    abstract Open(): Promise <boolean>;

    /**
     * Closes the storage and clears any in-memory data.
     */    
    abstract Close(): void;

    /**
     * Cleans the storage by removing all entries.
     * @returns A promise that resolves to `true` if the operation succeeds.
     */
    abstract Clean() : Promise <boolean>;

    /**
     * Retrieves a specific item from the storage.
     * @param key - The key of the item to retrieve.
     * @param target - The type of storage (`META` or `DATA`).
     * @returns A promise that resolves to the retrieved item, or `null` if not found.
     */    
    abstract Get(key: string, target: StorageType) : Promise <any>;

    /**
     * Saves or updates an item in the storage.
     * @param key - The key of the item to store.
     * @param data - The data to store.
     * @param target - The type of storage (`META` or `DATA`).
     * @returns A promise that resolves to `true` if the operation succeeds.
     */    
    abstract Set(key: string, data: any, target: StorageType) : Promise <boolean>;

    /**
     * Deletes an item from the storage.
     * @param key - The key of the item to delete.
     * @param target - The type of storage (`META` or `DATA`).
     * @returns A promise that resolves to `true` if the operation succeeds.
     */    
    abstract Del(key: string, target: StorageType) : Promise <boolean>;

    /**
     * Retrieves all items from the specified storage type.
     * @param target - The type of storage (`META` or `DATA`).
     * @returns A promise that resolves to an array of all items.
     */    
    abstract GetAll(target: StorageType) : Promise <Array<any>>;

    /**
     * Starts a transactional operation on the specified storage type.
     * @param target - The type of storage (`META` or `DATA`).
     * @throws An error if a transaction is already in progress.
     */    
    abstract Begin(target: StorageType): void;

    /**
     * Commits all changes made in a transaction to the storage.
     * @param target - The type of storage (`META` or `DATA`).
     * @returns A promise that resolves to `true` if the operation succeeds.
     * @throws An error if no transaction is in progress.
     */    
    abstract Flush(target: StorageType): Promise <boolean>
}

/************************************/
/* LOCAL DATABASE (STORAGE)         */
/************************************/

/**
 * Represents an in-memory cache-based database.
 */
export class CacheDatabase extends StorageDDBB {
    private _itemsmeta = new Map <string, any> ();
    private _itemsdata = new Map <string, any> ();

    private _stores: string [] = [];

    private _transdata: Array <any> | null = null;
    private _transmeta: Array <any> | null = null;

    /**
     * Creates an instance of CacheDatabase.
     * @param storagekey - The unique key for the storage instance.
     * @param store - The storage service used for persistence.
     */    
    constructor(private storagekey: string, private store: storeService){
        super();

        this._stores.push(storagekey + '_meta');
        this._stores.push(storagekey + '_data');
    }

    /**
     * Retrieves the name of the database.
     */    
    get ddbbname(): string {
        return this.storagekey;        
    }

    /** @inheritdoc */    
    async Open(): Promise <boolean> {
        const _metaitems = (await this.store.Get(this._stores[0])) || [];
        const _dataitems = (await this.store.Get(this._stores[1])) || [];

        for(const _item of _metaitems){
            this._itemsmeta.set(_item['__ddbb'], _item);
        }

        for(const _item of _dataitems){
            this._itemsdata.set(_item['__ddbb'], _item);
        }

        this._isopen = true;
        return true;
    }

    /** @inheritdoc */    
    Close(): void {
        this._isopen = false;

        this._itemsmeta.clear();
        this._itemsdata.clear();
    }

    /** @inheritdoc */    
    async Clean() : Promise <boolean> {        
        const _promises = [];
        for (const _store of this._stores){
            _promises.push(this.store.Del(_store));
        }
        await Promise.all(_promises); 

        this.Close();
        return true;
    }

    /** @inheritdoc */    
    async Get(key: string, target: StorageType) : Promise <any>{
        const _target: Map <string, any> = (target == 'META') ? this._itemsmeta : this._itemsdata;
        const _getitem = _target.get(key);
        if (_getitem){
            const result = Object.assign({}, _getitem);
            delete result.__ddbb;
            return result;
        }
        return null; 
    }

    /** @inheritdoc */    
    async Set(key: string, data: any, target: StorageType) : Promise <boolean> {
        const _store = (target == 'META') ? this._stores[0] : this._stores[1];
        const _target: Map <string, any> = (target == 'META') ? this._itemsmeta : this._itemsdata;

        if (data){
            const _oldata = this.Get(key, target);
            if (JSON.stringify(_oldata) == JSON.stringify(data)){
                return true;    // no changes;
            }
                
            data['__ddbb'] = key;

            const _atomic = (target === 'META') ? this._transmeta : this._transdata;
            if (_atomic !== null){
                _atomic.push(data);
            }
            else {
                _target.set(key, data);        
                try {
                    await this.store.Set(_store, Array.from(_target.values()));
                }
                catch(error){
                    console.error(error);
                    return false;
                }    
            }
        }
        else {
            this.Del(key, target);
        }

        return true;
    }

    /** @inheritdoc */    
    async Del(key: string, target: StorageType) : Promise <boolean> {
        const _store = (target == 'META') ? this._stores[0] : this._stores[1];
        const _target: Map <string, any> = (target == 'META') ? this._itemsmeta : this._itemsdata;

        if (_target.has(key)){
            _target.delete(key);

            try {
                await this.store.Set(_store, [ ..._target.values() ]);
            }
            catch(error){
                console.error(error);
                return false;
            }            
        }
        
        return true;
    }

    /** @inheritdoc */    
    async GetAll(target: StorageType) : Promise <Array<any>> {
        const _target = (target === 'META') ? this._itemsmeta : this._itemsdata;
        return Array.from(_target.values()).map(item => {
            const result = Object.assign({}, item);
            delete result.__ddbb;
            return result;            
        });
    }

    /** @inheritdoc */    
    Begin(target: StorageType): void {
        const _transaction = (target === 'META') ? this._transmeta : this._transdata;
        if (_transaction !== null){
            throw new Error("[STORAGE] Already in transaction")
        }

        if (target === 'META') {
            this._transmeta = [];
        } else {
            this._transdata = [];
        }
        
        if (target === 'META'){
            this._transmeta = [];
        }
        else {
            this._transdata = [];
        }
    }

    /** @inheritdoc */    
    async Flush(target: StorageType): Promise<boolean> {
        const _store = (target == 'META') ? this._stores[0] : this._stores[1];
        const _target: Map <string, any> = (target == 'META') ? this._itemsmeta : this._itemsdata;

        const _atomic = (target === 'META') ? this._transmeta : this._transdata;
        if ((_atomic === null) || (_atomic.length == 0)){
            throw new Error("[STORAGE] not in transaction or transaction is empty")
        }

        for(const _change of _atomic){
            _target.set(_change['__ddbb'], _change);      
        }

        try {
            await this.store.Set(_store, Array.from(_target.values()));
        }
        catch(error){
            console.error(error);
            return false;
        } 
        
        if (target === 'META'){
            this._transmeta = null;
        }
        else {
            this._transdata = null;
        }

        return true;
    }
}

/************************************/
/* LOCAL DATABASE (INDEXEDDB)       */
/************************************/

/**
 * Represents a database using the IndexedDB API.
 * Provides methods for CRUD operations and transaction handling.
 */
export class IndexedDDBB extends StorageDDBB {
    private _stores: string [] = [];

    private _transdata: Map <string, any> | null = null;
    private _transmeta: Map <string, any> | null = null;

    /**
     * Creates an instance of IndexedDDBB.
     * @param storagekey - The unique key for the IndexedDB storage.
     */    
    constructor(private storagekey: string){
        super();

        this._stores.push(storagekey + '_meta');
        this._stores.push(storagekey + '_data');
    }

    private _storage(type: StorageType): string {
        return this._stores[(type == 'META') ? 0 : 1];
    }

    /**
     * Retrieves the name of the database.
     */    
    get ddbbname(): string {
        return this.storagekey;        
    }

    private _database: IDBDatabase | null = null;

    /**
     * Retrieves the current IndexedDB instance.
     */    
    get database(): IDBDatabase | null {
        return this._database;
    }

    /** @inheritdoc */    
    async Open(): Promise <boolean> {
        try {
            this._database = await this.OpenDatabase(this.storagekey);
            this._isopen = true;
        }
        catch(error: any){
            console.error(error);
            this._isopen = false;
        }

        return this._isopen;
    }

    /** @inheritdoc */    
    Close(): void {
        this._isopen = false;

        if (this.database){
            this.CloseDatabase(this.database);
        }

        this._database = null;
    }

    /** @inheritdoc */    
    async Clean() : Promise <boolean> {
        try {
            return await this.CleanDatabase(this.storagekey);
        }
        catch(error: any){
            console.error(error);
            return false;
        }
    }

    /** @inheritdoc */    
    async Get(key: string, target: StorageType) : Promise <any> {
        if (!this.database){
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _transaction = this.database.transaction([ this._storage(target) ], 'readonly');
        try {
            const _request: IDBRequest = await this.RunInStore(_transaction.objectStore(this._storage(target)), 'get', key);
            if (_request){
                return _request.result || null;
            }
        }
        catch(error){
            console.error("[STORAGE] Error on database action: '" + error + "'");
        }

        return null;
    }

    /** @inheritdoc */    
    async Set(key: string, data: any, target: StorageType) : Promise <boolean> {
        if (!this.database){
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _atomic = (target === 'META') ? this._transmeta : this._transdata;
        if (_atomic !== null){
            _atomic.set(key, data);
        }
        else {
            const _transaction = this.database.transaction([ this._storage(target) ], 'readwrite');
            try {
                return !!(await this.RunInStore(_transaction.objectStore(this._storage(target)), 'put', data, key));
            }
            catch(error: any){
                console.error("[STORAGE] Error on database action: '" + error + "'");
            }

            return false;
        }

        return true;
    }

    /** @inheritdoc */    
    async Del(key: string, target: StorageType) : Promise <boolean> {
        if (!this.database){
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _transaction = this.database.transaction([ this._storage(target) ], 'readwrite');
        try {
            return !!(await this.RunInStore(_transaction.objectStore(this._storage(target)), 'delete', key));
        }
        catch(error: any){
            console.error("[STORAGE] Error on database action: '" + error + "'");
        }

        return false;
    }

    /** @inheritdoc */    
    async GetAll(target: StorageType) : Promise <Array<any>> {
        if (!this.database) {
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _transaction = this.database.transaction([ this._storage(target) ], 'readonly');
        try {
            const _request: IDBRequest = await this.RunInStore(_transaction.objectStore(this._storage(target)), 'getAll');
            if (_request){
                return _request.result;
            }            
        }
        catch(error: any){
            console.error("[STORAGE] Error on database action: '" + error + "'");
        }

        return [];
    }

    /** @inheritdoc */    
    Begin(target: StorageType): void {
        if (!this.database){
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _atomic = (target === 'META') ? this._transmeta : this._transdata;
        if (_atomic !== null){
            throw new Error("[STORAGE] Already in transaction")
        }

        if (target === 'META'){
            this._transmeta = new Map <string, any> ();
        }
        else {
            this._transdata = new Map <string, any> ();
        }
    }

    /** @inheritdoc */    
    async Flush(target: StorageType): Promise<boolean> {
        if (!this.database){
            throw new Error("[STORAGE] Database is not ready for operation");
        }

        const _atomic = (target === 'META') ? this._transmeta : this._transdata;
        if ((_atomic === null) || (_atomic.size == 0)){
            throw new Error("[STORAGE] not in transaction or transaction is empty")
        }

        const _transaction = this.database.transaction([ this._storage(target) ], 'readwrite');
        try {
            const _promises = [];
            for (const [key, _change] of _atomic.entries()) {
                _promises.push(this.RunInStore(_transaction.objectStore(this._storage(target)), 'put', _change, key))
            }

            const _result = (await Promise.all(_promises)).every((request) => !!request);
            if (_result) {
                if (target === 'META'){
                    this._transmeta = null;
                } 
                else {
                    this._transdata = null;
                }
            }

            return _result;
        }
        catch(error: any){
            console.error("[STORAGE] Error on database action: '" + error + "'");
        }

        return false;
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/

    private DatabaseRequest(storagekey: string) : IDBOpenDBRequest{
        const _request = indexedDB.open(storagekey);

        _request.onupgradeneeded = (event) => {
            const _db = (event.target as IDBOpenDBRequest).result;

            for (const _store of this._stores){
                if (!_db.objectStoreNames.contains(_store)) {
                    _db.createObjectStore(_store);
                }    
            }
        };

        _request.onblocked = (event) => {
            console.error("[STORAGE] The storage '" + storagekey + "' is blocked!!", event)
        }

        return _request;
    }

    private OpenDatabase(storagekey: string): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const _request: IDBOpenDBRequest = this.DatabaseRequest(storagekey);
            if (_request){
                _request.onerror = () => {
                    reject(_request.error || new Error(`Unknown error opening database '${storagekey}'`));
                }          

                _request.onsuccess = (event) => {                
                    resolve((event.target as IDBOpenDBRequest).result);
                }
            }
            else {
                reject("Error requesting for database '" + storagekey + "'");
            }
        })
    }

    private CloseDatabase(database: IDBDatabase): void {
        database.close();
    }

    private async CleanDatabase(storagekey: string) : Promise <boolean> {
        let database: IDBDatabase | null = null;
        try {
            database = this._database;
            if (!database) {
                database = await this.OpenDatabase(storagekey);
            }
    
            const _stores: string[] = [];
            for (let i = 0; i < database.objectStoreNames.length; i++) {
                _stores.push(database.objectStoreNames[i]);
            }

            if (_stores.length == 0){
                console.warn(`[STORAGE] No object stores to clean in database '${storagekey}'.`);
                return true;
            }
    
            const transaction = database.transaction(_stores, "readwrite");
            return await new Promise<boolean>((resolve, reject) => {
                
                transaction.oncomplete = () => {
                    console.info(`[STORAGE] Database '${storagekey}' content cleared successfully.`);
                    resolve(true);
                };
    
                transaction.onerror = (event) => {
                    console.error(
                        `[STORAGE] Error clearing database '${storagekey}':`,
                        (event.target as IDBTransaction).error
                    );
                    reject((event.target as IDBTransaction).error || new Error("Unknown transaction error."));
                };
    
                for (const store of _stores) {
                    transaction.objectStore(store).clear();
                }
            });
        } 
        catch (error) {
            console.error(`[STORAGE] Failed to clean database '${storagekey}':`, error);
            return false;
        } 
        finally {
            if (!this._database && database) {
                this.CloseDatabase(database);
            }
        }
    }

    private RunInStore(store: IDBObjectStore, action: string, ...args: any[]): Promise<IDBRequest> {
        return new Promise((resolve, reject) => {
            let _action: IDBRequest;

            try {
                if (typeof (store as any)[action] !== "function") {
                    return reject(new Error(`Invalid action '${action}' for IDBObjectStore`));
                }
                _action = (store as any)[action](...args);
            } 
            catch (error: any) {
                return reject(new Error(`Failed to execute action '${action}' on IDBObjectStore. Error: ${error.message}`));
            }
    
            _action.onsuccess = () => {
                resolve(_action);
            };
    
            _action.onerror = (event: Event) => {
                const error = (event.target as IDBRequest).error;
                console.error(`[STORAGE]: Error running action '${action}' in IndexedDB. Error:`, error);
                reject(error || new Error(`Unknown error running action '${action}'`));
            };
        });
    }    
}

/************************************/
/* CACHE SERVICE                    */
/************************************/

/**
 * Provides a service for managing storage in the browser.
 * Determines the best storage backend (IndexedDB or in-memory storage) and
 * provides an API for accessing, modifying, and managing data.
 */
@Injectable({
    providedIn: 'root'
})
export class cacheService {
    private _storage_databases = new Map <string, StorageDDBB>();

    /**
     * Creates an instance of cacheService.
     * @param store - A service for browser storage operations.
     */    
    constructor(private store: storeService){
        // nothing to do
    }

    /************************************/
    /* Determine impementation          */
    /************************************/

    /**
     * Selects the appropriate storage backend (IndexedDB or in-memory storage).
     * Creates and caches the database instance for future use.
     *
     * @param storagekey - A unique key identifying the storage.
     * @returns An instance of `StorageDDBB`.
     */    
    private database(storagekey: string){
        const _indexed = (window.indexedDB) && (('databases' in indexedDB));
        if (_indexed){
            return new IndexedDDBB(storagekey)
        }
        else {
            console.warn("[STORAGE] indexedDB databases API is not supported in this browser.")
            return new CacheDatabase(storagekey, this.store);
        }
    }

    /************************************/
    /* Storage access                   */
    /************************************/

    /**
     * Opens a storage backend for the given key.
     * If the storage does not exist, it is created and initialized.
     *
     * @param storagekey - A unique key identifying the storage.
     * @returns A promise resolving to the opened `StorageDDBB` instance.
     * @throws An error if the storage cannot be opened or initialized.
     */    
    async Open(storagekey: string): Promise <StorageDDBB> {
        let _database: StorageDDBB | undefined = this._storage_databases.get(storagekey);
        if (_database && _database.IsOpen){
            return _database;
        }

        _database = this.database(storagekey);
        if (!_database){
            throw new Error("[STORAGE] Could not create storage '" + storagekey + "'")
        }

        if (await _database.Open()){
            this._storage_databases.set(storagekey, _database);
            return _database;
        }
        else {
            throw new Error("[STORAGE] Could not open storage '" + storagekey + "'")
        }
    }
}

/************************************/
/* ITEM MANAGEMENT                  */
/************************************/

/**
 * Abstract base class for managing storage items.
 * Provides a structure for operations such as retrieving, setting, and deleting items.
 */
abstract class StorageItem {
    /**
     * Creates an instance of `StorageItem`.
     * @param storage - The storage backend used for operations.
     * @param indexfnc - A function to calculate the unique key for items.
     */    
    constructor(protected storage: StorageDDBB, protected indexfnc?: (data: any) => string){
        // nothing to do
    }

    /**
     * Retrieves an item from the storage by its key.
     * @param key - The key of the item to retrieve.
     * @returns A promise resolving to the retrieved item, or `null` if not found.
     */    
    abstract Get(key: string): Promise <any>;

    /**
     * Stores an item in the storage.
     * @param data - The data to store.
     * @returns A promise resolving to `true` if the operation was successful.
     */    
    abstract Set(data: any) : Promise <boolean>;

    /**
     * Deletes an item from the storage by its key.
     * @param key - The key of the item to delete.
     * @returns A promise resolving to `true` if the operation was successful.
     */
    abstract Del(key: string) : Promise <boolean>;
}

/**
 * Handles the management of metadata in the storage.
 * Extends `StorageItem` to provide operations specific to metadata.
 */
export class StorageMeta extends StorageItem {
    /**
     * Creates an instance of `StorageMeta`.
     * @param storage - The storage backend used for metadata operations.
     */    
    constructor(storage: StorageDDBB){
        super(storage, () => { return 'metadata'} )
    }

    /** @inheritdoc */  
    Get(): Promise <any> {
        if (!this.indexfnc){
            throw new Error("[CACHE] indexer function has not been provided!")
        }

        return this.storage.Get(this.indexfnc(null), 'META');
    }

    /** @inheritdoc */  
    Set(data: any): Promise <boolean> {
        if (!this.indexfnc){
            throw new Error("[CACHE] indexer function has not been provided!")        
        }

        return this.storage.Set(this.indexfnc(null), data, 'META');
    }

    /** @inheritdoc */  
    Del(): Promise <boolean> {
        if (!this.indexfnc){
            throw new Error("[CACHE] indexer function has not been provided!")        
        }

        return this.storage.Del(this.indexfnc(null), 'META');
    }
}

export class StorageData extends StorageItem {
    constructor(storage: StorageDDBB, indexfnc?: (data: any) => string){
        super(storage, indexfnc )
    }

    /** @inheritdoc */  
    async Get(key: string): Promise <any> {
        const _toread: any = await this.storage.Get(key, 'DATA');
        if (_toread){
            return this._Decrypt(_toread['data'])
        }
    }

    /** @inheritdoc */  
    async Set(data: any): Promise <boolean> {
        if (!this.indexfnc){
            throw new Error("[CACHE] indexer function has not been provided!")        
        }

        const _itemkey = this.indexfnc(data);
        const _olditem = await this.storage.Get(_itemkey, 'DATA');
        
        const _olddata = _olditem && ('data' in _olditem) ? _olditem['data'] : null;
        let _newdata = data;

        if (_olddata){
            const _olddate: number = ('updated' in _olditem) ? _olditem['updated'] : 0;  
            
            let _newdate: number = new Date().getTime();
            if ('updated' in data){
                const _datestr = GenericUtils.mysqlToDateStr(data['updated']);
                if (_datestr){
                    _newdate = new Date(_datestr).getTime();
                }
            }

            if (_newdate > _olddate){
                _newdata = this._Merge(this._Decrypt(_olddata), _newdata);
            }
        }

        const _towrite = {
            updated: new Date(('updated' in data) ? data['updated'] : null).getTime(),
            data: this._Encrypt(_newdata)
        }

        return this.storage.Set(_itemkey, _towrite, 'DATA');
    }

    /** @inheritdoc */  
    Del(key: string): Promise <boolean> {
        return this.storage.Del(key, 'DATA');
    }

    /** @inheritdoc */  
    async GetAll(): Promise<Array<any>> {
        const _encrypted = await this.storage.GetAll('DATA');
        const _decrypted = [];
        for (const _item of _encrypted){
            _decrypted.push(this._Decrypt(_item.data))
        }
        return _decrypted;
    }

    /**********************************/
    /* PRIVATE METHODS                */
    /**********************************/

    /**
     * Merges two data objects, preserving existing fields and concatenating arrays.
     * @param olditem - The existing data object.
     * @param newitem - The new data object to merge.
     * @returns The merged data object.
     */    
    private _Merge(olditem: any, newitem: any){
        if (!olditem){
            return newitem;     // if no previous item, then merge is the new item
        }

        const _merged = olditem;

        for(const _field in newitem){
            if (!(_field in olditem)){
                _merged[_field] = newitem[_field];
                continue;   // not in old item (copied from new)
            }

            // if not an array: replace with new content
            if (!(newitem[_field] instanceof Array)){
                _merged[_field] = newitem[_field];
            }

            // concatenate arrays (do not loose old items)
            else {
                _merged[_field] = _merged[_field].concat(newitem[_field]);
                _merged[_field] = [...new Set(_merged[_field])];
            }
        }

        return _merged;
    }

    /**
     * Encrypts a data object into a Base64 string with obfuscation.
     * @param data - The data to encrypt.
     * @returns The encrypted string.
     */    
    private _Encrypt(data: any){
        let _data = '';

        const _b64 = GenericUtils.stringToBase64(JSON.stringify(data));
        if (_b64){
            const _pos: Array <number> = [ -1, -1, -1 ];

            for (let _idx = 0; _idx < _pos.length; _idx++) {
                let _candidate = Math.floor(Math.random() * Math.min(_b64.length, 16));
                while (_pos.includes(_candidate)) {
                    _candidate = Math.floor(Math.random() * Math.min(_b64.length, 16));
                }
                _pos[_idx] = _candidate;
            }

            _pos.sort((a:number, b:number) => {
                return a - b
            });

            for (let _idx = 0; _idx < _pos.length; _idx++) {
                _data += _pos[_idx].toString(16) + _b64[_pos[_idx]];
            }

            let _offset = 0;
            for (let _idx = 0; _idx < _pos.length; _idx++) {
                _data += _b64.slice(_offset, _pos[_idx]);
                _offset = _pos[_idx] + 1;
            }
            _data += _b64.slice(_offset);
        }

        return _data;
    }

    /**
     * Decrypts an obfuscated Base64 string into a data object.
     * @param data - The encrypted string to decrypt.
     * @returns The decrypted data object.
     */    
    private _Decrypt(data: string): any{
        let _data = '';

        const _pos: Array <number> = [ 
            parseInt(data[0], 16),
            parseInt(data[2], 16),
            parseInt(data[4], 16)
        ];

        const _val: Array <string> = [
            data[1],
            data[3],
            data[5]
        ]

        const _b64 = data.slice(6).split('');
        for (let _idx = 0; _idx < _pos.length; _idx++) {
            _b64.splice(_pos[_idx], 0, _val[_idx]);
        }
        _data = _b64.join('');

        const _item = GenericUtils.base64ToString(_data);
        return _item ? JSON.parse(_item) : null;
    }    
}